import type { DocumentLink } from "@/models/document-link/DocumentLink";

function normalizeIdList(ids?: string[]): string[] {
  return [...(ids ?? [])]
    .filter(Boolean)
    .map((s) => s.trim())
    .sort();
}

function sameOptionalString(a?: string | null, b?: string | null): boolean {
  return (a ?? "").trim() === (b ?? "").trim();
}

function isOutgoingFromDocument(link: DocumentLink, documentId: string): boolean {
  return link.originDocumentId === documentId;
}

/**
 * True when the URL carries reference-navigation highlight params that match this link
 * for the open document (same fields as encoded when navigating from the references tab).
 */
export function documentLinkMatchesUrlHighlight(
  link: DocumentLink,
  documentId: string,
  highlightIds: string[],
  refMatchText: string | undefined,
  refChunkText: string | undefined
): boolean {
  const hasUrlHint =
    highlightIds.length > 0 ||
    Boolean(refMatchText?.trim()) ||
    Boolean(refChunkText?.trim());

  if (!hasUrlHint) return false;

  const isOutgoing = isOutgoingFromDocument(link, documentId);
  const sideIds = isOutgoing ? link.originHtmlIds : link.destinationHtmlIds;
  const matchText = isOutgoing
    ? link.originUniqueMatchText
    : link.destinationUniqueMatchText;
  const chunkText = isOutgoing
    ? link.originUniqueChunkText
    : link.destinationUniqueChunkText;

  const urlIdsNorm = normalizeIdList(highlightIds);
  const linkIdsNorm = normalizeIdList(sideIds);

  const idsEqual =
    urlIdsNorm.length === linkIdsNorm.length &&
    urlIdsNorm.every((id, i) => id === linkIdsNorm[i]);

  return (
    idsEqual &&
    sameOptionalString(refMatchText, matchText) &&
    sameOptionalString(refChunkText, chunkText)
  );
}
